import { ButtonPrimary, ButtonTertiary, IconButton, RadioButton } from "@nova/component-btn"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { Select } from "@nova/component-select"
import { CustomFieldSpec, CustomFieldType } from "@nova/schemas"
import classNames from "classnames"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { useDrag, useDragLayer, useDrop } from "react-dnd"
import { emptyFieldSpec, FieldControls } from "./CustomFieldBuilderTypes"
import { FieldBodyByType, FieldFooter } from "./CustomFieldInputs"
import { useUpsertCustomFieldDefinition } from "./CustomFieldsQueries"

const FieldCard = ({
    spec,
    fieldControls,
    index,
    autoFocus,
}: {
    spec: CustomFieldSpec
    fieldControls: FieldControls
    index: number
    autoFocus: boolean
}) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const dragHandleRef = useRef<HTMLDivElement>(null)

    const [, drop] = useDrop({
        accept: "CUSTOM_FIELD",
        hover: (item: { index: number; id: string }, monitor) => {
            if (!ref.current) return
            const dragIndex = item.index
            const hoverIndex = index
            if (dragIndex === hoverIndex) return

            const hoverBoundingRect = ref.current.getBoundingClientRect()
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
            const clientOffset = monitor.getClientOffset()
            const hoverClientY = (clientOffset?.y ?? 0) - hoverBoundingRect.top

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

            const direction = dragIndex < hoverIndex ? 1 : -1
            if (Math.abs(dragIndex - hoverIndex) !== 1) return

            fieldControls.moveField(item.id, { direction, skipAnimationFor: item.id })
            item.index = dragIndex + direction
        },
    })

    const [, drag, preview] = useDrag({
        type: "CUSTOM_FIELD",
        item: { id: spec.id, index },
    })

    const { isDragging, isAnythingDragging } = useDragLayer((monitor) => ({
        isDragging: monitor.isDragging() && monitor.getItem()?.id === spec.id,
        isAnythingDragging: monitor.isDragging(),
    }))

    drag(dragHandleRef)
    drop(preview(ref))

    const setCardRef = (node: HTMLDivElement | null) => {
        ref.current = node
        fieldControls.registerCard(spec.id, node)
    }

    const FieldBody = FieldBodyByType[spec.type] as FC<{
        spec: CustomFieldSpec
        fieldControls: FieldControls
        autoFocus: boolean
    }>

    return (
        <div
            ref={setCardRef}
            className={classNames(
                "w-full flex justify-center group transition-opacity select-none",
                {
                    "opacity-30": isDragging,
                }
            )}
        >
            <div className="w-full flex gap-1">
                <div className="flex flex-col opacity-0 group-hover:opacity-100 items-center shrink-0">
                    <IconButton
                        onClick={() => fieldControls.moveField(spec.id, { direction: -1 })}
                        disabled={index === 0}
                        className="text-gray-100"
                    >
                        <Icons.SmallIcon Icon={Icons.ArrowDropUp} />
                    </IconButton>

                    <div
                        ref={dragHandleRef}
                        data-drag-type="touch"
                        className="cursor-grab active:cursor-grabbing text-gray-100"
                    >
                        <Icons.SmallIcon Icon={Icons.DragHandle} />
                    </div>

                    <IconButton
                        onClick={() => fieldControls.moveField(spec.id, { direction: 1 })}
                        disabled={fieldControls.getNumFields() === index + 1}
                        className="text-gray-100"
                    >
                        <Icons.SmallIcon Icon={Icons.ArrowDropdown} />
                    </IconButton>
                </div>
                <div
                    className={classNames(
                        "flex-1 divide-y divide-white-100 rounded-xl border border-white-100",
                        {
                            "group-hover:ring-2 group-hover:ring-white-300 focus-within:ring-2 focus-within:ring-white-300":
                                !isDragging && !isAnythingDragging,
                            "ring-2 ring-blue-500": isDragging,
                        }
                    )}
                >
                    <div className="p-8 flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <div>Type*</div>
                            <div className="w-fit">
                                <Select<CustomFieldType>
                                    onChange={(v) =>
                                        v !== spec.type &&
                                        fieldControls.editField(spec.id, emptyFieldSpec(v))
                                    }
                                    options={[
                                        { label: "Text Input", value: "text" },
                                        { label: "Yes / No", value: "yes-no" },
                                        { label: "Multiple Choice", value: "multiple-choice" },
                                    ]}
                                    value={spec.type}
                                />
                            </div>
                        </div>
                        <FieldBody
                            spec={spec}
                            fieldControls={fieldControls}
                            autoFocus={autoFocus}
                        />
                    </div>
                    <FieldFooter spec={spec} fieldControls={fieldControls} />
                </div>
            </div>
        </div>
    )
}

export const CustomFieldBuilder = ({
    initialForm,
    onSaved,
    onCancel,
}: {
    initialForm: CustomFieldSpec[]
    onSaved: () => void
    onCancel: () => void
}) => {
    const [form, setForm] = useState<CustomFieldSpec[]>(
        initialForm.length ? initialForm : [emptyFieldSpec("text")]
    )
    const [focusedFieldId, setFocusedFieldId] = useState<string | null>(null)
    const { upsertCustomFieldDefinition, isMutating } = useUpsertCustomFieldDefinition()

    const cardRefs = useRef(new Map<string, HTMLElement>())

    const registerCard = useCallback<FieldControls["registerCard"]>((id, node) => {
        if (node) cardRefs.current.set(id, node)
        else cardRefs.current.delete(id)
    }, [])

    const moveField = useCallback<FieldControls["moveField"]>(
        (id, { direction, skipAnimationFor }) => {
            const firstRects = new Map<string, number>()
            cardRefs.current.forEach((node, fieldId) => {
                firstRects.set(fieldId, node.getBoundingClientRect().top)
            })

            flushSync(() => {
                setForm((prev) => {
                    const index = prev.findIndex((f) => f.id === id)
                    if (index === -1) return prev
                    if (index === 0 && direction === -1) return prev
                    if (index === prev.length - 1 && direction === 1) return prev
                    const copy = [...prev]
                    const fieldToMove = copy[index]
                    copy[index] = copy[index + direction]
                    copy[index + direction] = fieldToMove
                    return copy
                })
            })

            cardRefs.current.forEach((node, fieldId) => {
                if (fieldId === skipAnimationFor) return
                const firstTop = firstRects.get(fieldId)
                if (firstTop === undefined) return
                const delta = firstTop - node.getBoundingClientRect().top
                if (!delta) return
                node.style.transition = "none"
                node.style.transform = `translateY(${delta}px)`
                requestAnimationFrame(() => {
                    node.style.transition = "transform 150ms ease"
                    node.style.transform = ""
                })
            })
        },
        []
    )

    const fieldControls = useMemo<FieldControls>(
        () => ({
            editField: (id, spec) => setForm((prev) => prev.map((f) => (f.id === id ? spec : f))),
            deleteField: (id) => setForm((prev) => prev.filter((f) => f.id !== id)),
            moveField,
            getNumFields: () => form.length,
            registerCard,
        }),
        [moveField, registerCard, form.length]
    )

    const addField = (type: CustomFieldType) => {
        const spec = emptyFieldSpec(type)
        setForm((prev) => [...prev, spec])
        setFocusedFieldId(spec.id)
    }

    const isFormValid = form.every(
        (field) =>
            field.label.trim() !== "" &&
            (field.type !== "multiple-choice" ||
                (field.options.length > 0 && field.options.every((o) => o.trim() !== "")))
    )

    const handleSave = async () => {
        await upsertCustomFieldDefinition({ form })
        onSaved()
    }

    return (
        <div className="flex flex-col gap-4">
            <ScrollableArea className="max-h-[60vh]" shouldPreventScrollbarShifting>
                <div className="flex flex-col items-center gap-4 w-full pr-3 pt-1 overflow-hidden">
                    {form.map((field, i) => (
                        <FieldCard
                            key={field.id}
                            spec={field}
                            fieldControls={fieldControls}
                            index={i}
                            autoFocus={field.id === focusedFieldId}
                        />
                    ))}
                    <Dropdown>
                        <Dropdown.Trigger className="h-8 flex gap-1 items-center justify-center w-full group">
                            <div className="flex-1 h-px bg-gray-700 group-hover:bg-gray-600 transition-colors" />
                            <Icons.SmallIcon
                                className="text-white-500 group-hover:text-white group-hover:scale-110 transition-all"
                                Icon={Icons.Plus}
                            />
                            <div className="flex-1 h-px bg-gray-700 group-hover:bg-gray-600 transition-colors" />
                        </Dropdown.Trigger>
                        <Dropdown.Menu position="top-center">
                            <Dropdown.ButtonItem className="gap-2" onClick={() => addField("text")}>
                                <Icons.SmallIcon Icon={Icons.TextSelectStart} />
                                Text Input
                            </Dropdown.ButtonItem>
                            <Dropdown.ButtonItem
                                className="gap-2"
                                onClick={() => addField("yes-no")}
                            >
                                <Icons.SmallIcon Icon={Icons.CheckCircle} />
                                Yes / No
                            </Dropdown.ButtonItem>
                            <Dropdown.ButtonItem
                                className="gap-2"
                                onClick={() => addField("multiple-choice")}
                            >
                                <div className="flex items-center justify-center w-5 h-5">
                                    <RadioButton isSelected onClick={() => {}} size="small" />
                                </div>
                                Multiple Choice
                            </Dropdown.ButtonItem>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </ScrollableArea>
            <div className="w-full flex justify-end items-center gap-2">
                <ButtonTertiary title="Cancel" onClick={onCancel} disabled={isMutating} />
                <ButtonPrimary
                    title="Save"
                    onClick={handleSave}
                    disabled={isMutating || !isFormValid}
                    isLoading={isMutating}
                />
            </div>
        </div>
    )
}
